/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * PullToRefreshContainer - Wrapper component for pull-to-refresh
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { motion, AnimatePresence } from 'framer-motion'
import PropTypes from 'prop-types'
import { usePullToRefresh } from '../hooks/usePullToRefresh'
import { useTouchDevice } from '../hooks/useTouchDevice'
import PullToRefreshSpinner from './PullToRefreshSpinner'

/**
 * Wrapper component that provides pull-to-refresh functionality
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to wrap
 * @param {Function} props.onRefresh - Callback when refresh is triggered
 * @param {boolean} props.enabled - Whether pull-to-refresh is enabled
 * @param {string} props.className - Additional CSS classes
 */
const PullToRefreshContainer = ({ children, onRefresh, enabled = true, className = '' }) => {
  const isTouchDevice = useTouchDevice()

  const { containerRef, pullDistance, progress, isRefreshing, isReadyToRefresh } = usePullToRefresh(
    {
      onRefresh,
      enabled: enabled && isTouchDevice
    }
  )

  // Don't show pull UI on non-touch devices
  const showPullIndicator = isTouchDevice && (pullDistance > 0 || isRefreshing)

  const contentOffset = isRefreshing ? 110 : pullDistance > 0 ? Math.min(pullDistance, 110) : 0

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
      {/* Pull indicator - centered in the gap above content */}
      <AnimatePresence>
        {showPullIndicator && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-x-0 top-0 flex flex-col items-center justify-center pb-6 pointer-events-none"
            style={{ height: contentOffset }}
          >
            <div
              className={`
                flex items-center justify-center
                bg-white dark:bg-gray-800
                rounded-full shadow-lg
                p-3
                transition-colors duration-200
                ${isReadyToRefresh ? 'bg-primary-50 dark:bg-primary-900/30' : ''}
              `}
            >
              <PullToRefreshSpinner progress={progress} isRefreshing={isRefreshing} size={28} />
            </div>

            {/* "Release to refresh" text */}
            {isReadyToRefresh && !isRefreshing && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                Release to refresh
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content pushed down during pull */}
      <motion.div
        className="h-full"
        animate={{ y: contentOffset }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      >
        {children}
      </motion.div>
    </div>
  )
}

PullToRefreshContainer.propTypes = {
  children: PropTypes.node.isRequired,
  onRefresh: PropTypes.func.isRequired,
  enabled: PropTypes.bool,
  className: PropTypes.string
}

export default PullToRefreshContainer
